import { Link } from '@innovationnorway/cl-react';
import { ReactNode, useContext } from 'react';
import { AppContext } from '../context/app-context';
import { LocalizationContext, LocalizeOptions } from '../context/localization-context';
import { Language, languageToLocaleMap } from '../models';

// TODO we can add type safety on nameSpace as well, by refactoring to inject the object as type T instead of string
const useLocalizer = (namespace: string) => {
  const ctx = useContext(LocalizationContext);
  const { state } = useContext(AppContext);

  if (!ctx) {
    throw new Error('useLocalizer must be used within a LocalizationProvider');
  }

  /**
   * @deprecated use typeSafeL instead
   */
  const l = (key: string, options?: LocalizeOptions) => ctx(namespace, key, options);

  /**
   * @deprecated use typeSafeL instead
   */
  const L = (key: string) => l(key, { capitalize: true });

  /**
   * @deprecated use typeSafeL instead
   */
  const globalL = (key: string, options?: LocalizeOptions) => ctx('Global', key, options);

  /**
   * Type safe version of l using template literal keys of types. Object type T has to be an object literal, suffixed with "as const" assertion.
   * Returns string if no replacements are provided, otherwise returns Array<string | ReactNode>.
   * If replacements are provided, the localization string must contain placeholders like {0}, {1}, etc.
   * The placeholders will be replaced with the corresponding items from the replacements array.
   * @param key A template literal key existing of type T
   * @example typeSafeL<typeof LoanDetails>("formActionsTitle")
   * @example typeSafeL<typeof LoanDetails>("welcomeMessage", {replacements: ["John", <span>Click here</span>]})
   */
  function typeSafeL<T>(key: keyof T extends string ? keyof T : never, options?: Omit<LocalizeOptions, 'replacements'>): string;
  function typeSafeL<T>(key: keyof T extends string ? keyof T : never, options: LocalizeOptions & { replacements: Array<string | ReactNode> }): Array<string | ReactNode>;
  function typeSafeL<T>(key: keyof T extends string ? keyof T : never, options?: LocalizeOptions & { replacements?: Array<string | ReactNode> }): Array<string | ReactNode> | string {
    const text = ctx(namespace, key, options);

    if (options?.replacements && options.replacements.length > 0) {
      return interpolateString(text, options.replacements);
    }

    return text;
  }

  function getCurrentLanguage(): Language {
    return state.configuration.language;
  }

  function getCurrentLocale(): string {
    if (languageToLocaleMap[state.configuration.language]) {
      return languageToLocaleMap[state.configuration.language];
    }

    return languageToLocaleMap[Language.en];
  }

  return { l, L, globalL, typeSafeL, getCurrentLanguage, getCurrentLocale };
};

export { useLocalizer };

// Retroactive types to prevent regressions. We cannot use interface due to existing code.
// TODO swap out with interfaces
type useLocalizerReturnType = ReturnType<typeof useLocalizer>;
export type localizerFunction = useLocalizerReturnType['l'];
export type typeSafeLocalizerFunction = useLocalizerReturnType['typeSafeL'];

/**
 * Interpolates a string with replacements.
 * Replaces patterns like {0}, {1}, etc. with corresponding items from the replacements array.
 */
const interpolateString = (text: string, replacements?: Array<string | ReactNode>) => {
  if (!replacements || replacements.length === 0 || !text.includes('{')) {
    return text;
  }

  const segments = text.split(/(\{[0-9]+\})/g);
  return segments.map((segment) => {
    const match = segment.match(/\{([0-9]+)\}/);
    if (match) {
      const replacementIndex = parseInt(match[1], 10);
      return replacementIndex < replacements.length ? replacements[replacementIndex] : segment;
    }
    return segment;
  });
};

export const createLink = (href: string, text: string): ReactNode => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {text}
  </a>
);

export const createLinkKoble = (href: string, text: string): ReactNode => (
  <Link href={href} target="_blank" rel="noopener noreferrer" key={href}>
    {text}
  </Link>
);
